import { motion } from 'framer-motion';
import { Linkedin, Mail, ArrowUpRight } from 'lucide-react';
import { TEST_IDS } from '@/constants/testIds';

const FOOTER_NAV = [
  { label: 'Method', href: '#method' },
  { label: 'Credentials', href: '#authority' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Process', href: '#process' },
  { label: 'FAQ', href: '#faq' },
];

export default function FooterSection() {
  const handleClick = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer id="footer" data-testid={TEST_IDS.footerSection} className="bg-[#1B2A4A] text-[#F7F5F2] py-16 md:py-20 px-6 md:px-12 relative">
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-serif text-2xl md:text-3xl text-[#F7F5F2] mb-4">
              Sarah Watson
            </h3>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C9A96E] font-sans font-semibold mb-6">
              Luminous Coaching
            </p>
            <p className="text-sm text-[#F7F5F2]/50 font-sans leading-relaxed max-w-sm">
              Executive coaching for creative leaders. Turning burnout and dysfunction into clarity and breakthrough.
            </p>
          </motion.div>

          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-[#C9A96E] font-sans font-semibold mb-6">
              Navigate
            </p>
            <div className="flex flex-col gap-3">
              {FOOTER_NAV.map((item, i) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleClick(e, item.href)}
                  className="text-sm text-[#F7F5F2]/50 font-sans hover:text-[#C9A96E] transition-colors duration-300"
                  whileHover={{ x: 4 }}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.05 }}
                >
                  {item.label}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Connect */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-[#C9A96E] font-sans font-semibold mb-6">
              Connect
            </p>
            <div className="flex flex-col gap-4">
              <motion.a
                href="https://linkedin.com/in/sarahwatson"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TEST_IDS.footerLinkedin}
                className="flex items-center gap-3 text-sm text-[#F7F5F2]/50 font-sans hover:text-[#C9A96E] transition-colors duration-300 group"
                whileHover={{ x: 4 }}
              >
                <Linkedin className="w-4 h-4" />
                LinkedIn
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.a>
              <motion.a
                href="mailto:hello@luminouscoaching.com"
                data-testid={TEST_IDS.footerEmail}
                className="flex items-center gap-3 text-sm text-[#F7F5F2]/50 font-sans hover:text-[#C9A96E] transition-colors duration-300"
                whileHover={{ x: 4 }}
              >
                <Mail className="w-4 h-4" />
                hello@luminouscoaching.com
              </motion.a>
            </div>
          </motion.div>
        </div>

        {/* Bottom */}
        <motion.div
          className="pt-8 border-t border-[#F7F5F2]/8 flex flex-col md:flex-row items-center justify-between gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="text-xs text-[#F7F5F2]/25 font-sans">
            &copy; {new Date().getFullYear()} Sarah Watson Luminous Coaching. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-[#F7F5F2]/25 font-sans hover:text-[#C9A96E] transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-xs text-[#F7F5F2]/25 font-sans hover:text-[#C9A96E] transition-colors">
              Terms
            </a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
